/**
 * SRC-20 balance reveal — single shielded signed-read via hand-built envelope.
 *
 * Why not seismic-viem's `sreadContract`? In the monorepo's setup it was
 * delivering a *plain* eth_call (no envelope) to the chain — Seismic then zeroed
 * `from`, `isOwner(address(0))` returned false, and the reader reverted with
 * "caller is not a safe owner" even when the connected EOA was an owner.
 * The PoC's hand-built type-`0x4A` envelope is the known-working path; spec §0
 * still applies (one MetaMask EIP-712 prompt, no second signature).
 */
import type { Address, Hex } from 'viem'
import { decodeAbiParameters, encodeFunctionData, bytesToHex as viemBytesToHex } from 'viem'
import type { Eip1193Provider } from 'ethers'
import { READER_ABI, READER_REVERT_REGEX, READER_REVERT_TAILS_TO_ERROR } from '../constants'
import type { Src20ReaderError } from '../types'
import { signedShieldedRead } from '../seismic/signedRead'

type ReadShieldedBalanceParams = {
  chainId: number
  rpcUrl: string
  provider: Eip1193Provider
  signer: Address
  reader: Address
  safe: Address
  token: Address
}

const extractReaderRevertTail = (err: unknown): string | undefined => {
  const visit = (e: unknown, depth: number): string | undefined => {
    if (depth > 8 || !e || typeof e !== 'object') return undefined
    const obj = e as Record<string, unknown>
    for (const key of ['shortMessage', 'details', 'message', 'reason'] as const) {
      const v = obj[key]
      if (typeof v === 'string') {
        const m = v.match(READER_REVERT_REGEX)
        if (m) return m[1].trim()
      }
    }
    return visit(obj.cause, depth + 1)
  }
  return visit(err, 0)
}

export const mapReaderError = (err: unknown): Src20ReaderError => {
  if (err && typeof err === 'object') {
    const e = err as { code?: number; name?: string; message?: string }
    if (e.code === 4001 || e.name === 'UserRejectedRequestError') return { kind: 'userRejected' }
    if (typeof e.message === 'string' && /user (rejected|denied)/i.test(e.message)) {
      return { kind: 'userRejected' }
    }
  }

  const tail = extractReaderRevertTail(err)
  if (tail && READER_REVERT_TAILS_TO_ERROR[tail]) return READER_REVERT_TAILS_TO_ERROR[tail]()

  if (err && typeof err === 'object') {
    const msg = (err as { message?: string }).message
    if (typeof msg === 'string') {
      // Match real network failures only. Avoid a bare `fetch`, which would also match the
      // `[fetchTeePubkey]`/`[fetchLatestBlock]` step prefixes added by signedRead's `wrap()` and
      // misclassify genuine TEE/RPC logic errors from those steps as network errors.
      if (/network|failed to fetch|timeout|disconnect/i.test(msg)) return { kind: 'network', message: msg }
      if (/decrypt|invalid tag|gcm/i.test(msg)) return { kind: 'decryption', message: msg }
      return { kind: 'unknown', raw: msg }
    }
  }
  return { kind: 'unknown', raw: String(err) }
}

export const readShieldedBalance = async ({
  chainId,
  rpcUrl,
  provider,
  signer,
  reader,
  safe,
  token,
}: ReadShieldedBalanceParams): Promise<bigint> => {
  const calldata: Hex = encodeFunctionData({
    abi: READER_ABI,
    functionName: 'read',
    args: [safe, token],
  })

  const plaintext = await signedShieldedRead({
    chainId,
    rpcUrl,
    owner: signer,
    provider,
    to: reader,
    calldata,
  })

  if (plaintext.length < 32) {
    throw new Error(
      `Reader response too short after decrypt: ${plaintext.length} bytes — likely a revert or unencrypted response.`,
    )
  }
  const [value] = decodeAbiParameters([{ type: 'uint256' }], viemBytesToHex(plaintext))
  return value as bigint
}
